import type {
    CreatePolicyTypeDTO,
    UpdatePolicyTypeDTO,
} from '@/modules/hr/company-policy/domain/types';
import {
    Button,
    FormInput,
    FormNumber,
    FormRoot,
    FormTextarea,
    RHF,
    toast,
    zodResolver,
} from '@devhop/ui';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { policyTypeFormSamples } from './policyTypeFormSamples';

const PolicyTypeFormSchema = z.object({
    name: z.string().min(1, 'ກະລຸນາປ້ອນຊື່ປະເພດນະໂຍບາຍ'),
    description: z.string().optional().nullable(),
    orderIndex: z.number().int().default(0),
});

export type PolicyTypeFormValues = z.infer<typeof PolicyTypeFormSchema>;

export function PolicyTypeForm({
    initialValues,
    onSubmit,
    submitting,
}: {
    initialValues?: Partial<PolicyTypeFormValues>;
    onSubmit: (values: CreatePolicyTypeDTO | UpdatePolicyTypeDTO) => void;
    submitting?: boolean;
}) {
    const [sampleIndex, setSampleIndex] = useState(0);
    const isDevMode =
        typeof import.meta !== 'undefined' &&
        (import.meta.hot !== undefined ||
            import.meta.env?.MODE === 'development');
    const isCreateMode = initialValues == null;

    const methods = RHF.useForm<PolicyTypeFormValues>({
        // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
        resolver: zodResolver(PolicyTypeFormSchema) as any,
        defaultValues: {
            name: '',
            description: null,
            orderIndex: 0,
            ...initialValues,
        },
    });

    const handleAutoFillSample = () => {
        const sample = policyTypeFormSamples[sampleIndex];
        if (!sample) return;

        methods.setValue('name', sample.name);
        methods.setValue('description', sample.description ?? null);
        methods.setValue('orderIndex', sample.orderIndex ?? 0);

        setSampleIndex((prev) => (prev + 1) % policyTypeFormSamples.length);
        toast.success('Auto-filled form with sample data');
    };

    return (
        <FormRoot<PolicyTypeFormValues>
            methods={methods}
            onSubmit={(vals) =>
                onSubmit({
                    name: vals.name,
                    description: vals.description ?? null,
                    orderIndex: vals.orderIndex ?? 0,
                })
            }
            className="space-y-4"
        >
            <FormInput
                name="name"
                label="ຊື່ປະເພດນະໂຍບາຍ"
                requiredMark
                placeholder="ຕົວຢ່າງ: ລະບຽບການລາ, ລະບຽບ OT"
            />
            <FormTextarea
                name="description"
                label="ຄຳອະທິບາຍ"
                placeholder="ຄຳອະທິບາຍ (ບໍ່ບັງຄັບ)"
                rows={3}
            />
            <FormNumber
                name="orderIndex"
                label="ລຳດັບ"
                placeholder="0"
                hint="ລຳດັບສຳລັບການຈັດລຽງ (ຕໍ່າກ = ສະແດງກ່ອນ)"
            />
            <div className="flex items-center justify-between gap-2">
                {isDevMode &&
                isCreateMode &&
                policyTypeFormSamples.length > 0 ? (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAutoFillSample}
                        className="gap-1.5"
                    >
                        <Sparkles className="h-4 w-4" />
                        ຕື່ມຕົວຢ່າງ ({sampleIndex + 1}/
                        {policyTypeFormSamples.length})
                    </Button>
                ) : (
                    <div />
                )}
                <Button
                    type="button"
                    onClick={methods.handleSubmit(onSubmit)}
                    disabled={submitting}
                    isLoading={submitting}
                >
                    ບັນທຶກ
                </Button>
            </div>
        </FormRoot>
    );
}
